(function () {
  'use strict';

  // ── Constants ────────────────────────────────────────────────────────────
  const DEFAULT_CENTER  = { lat: 43.6532, lng: -79.3832 }; // Toronto
  const DEFAULT_ZOOM    = 10;
  const DETAIL_ZOOM     = 15;
  const MAX_CARDS       = 3;
  // Info-window sizing. With no maxWidth set, Google clamps the bubble to the
  // map, which on a phone falls under the width the actions row needs and drops
  // the phone number onto its own line. Grant it explicitly instead: as wide as
  // the map allows, less a gutter that keeps the bubble clear of the map edges,
  // capped by MAX so it does not sprawl on tablets. Desktop is unaffected — the
  // CSS still caps the content at 260px above 1023px.
  //
  // The gutter is deliberately small: every pixel here comes off the actions
  // row, which needs ~270px for "Get directions" plus a formatted phone number.
  // The mobile CSS strips Google's own padding off gm-style-iw-c/-d and
  // re-applies it on .store-info-window, so the row gets mapWidth minus gutter
  // minus that padding. Under 350px the CSS drops the padding to 24px and the
  // gutter halves, which is what keeps the row on one line down to 320px.
  const INFO_MAX_WIDTH        = 380;
  const INFO_MAP_GUTTER       = 16;
  const INFO_NARROW_VIEWPORT  = 350;
  const INFO_NARROW_GUTTER    = 8;
  // Full day name prefixes matching production flat-field schema
  // (e.g. store.monday_open, store.monday_close, store.monday_is_open)
  const DAY_NAMES       = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];

  // ── Haversine distance (km) ──────────────────────────────────────────────
  function haversine(lat1, lng1, lat2, lng2) {
    const R    = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a    = Math.sin(dLat / 2) ** 2 +
                 Math.cos(lat1 * Math.PI / 180) *
                 Math.cos(lat2 * Math.PI / 180) *
                 Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  // ── Today's hours for a store ────────────────────────────────────────────
  // Production data uses flat fields: store.monday_open, store.monday_close,
  // store.monday_is_open (not a nested store.hours object).
  function getTodayHours(store) {
    const tz      = store.timezone || 'America/Toronto';
    // Resolve current day index in the store's local timezone via Intl
    const dayIdx  = new Date(
      new Date().toLocaleString('en-US', { timeZone: tz })
    ).getDay(); // 0=Sun, 1=Mon, ...
    const dayName = DAY_NAMES[dayIdx]; // e.g. 'monday'

    const isOpen  = store[dayName + '_is_open'];
    const openStr = store[dayName + '_open'];
    const closeStr= store[dayName + '_close'];

    if (!isOpen || !openStr || !closeStr) return null;

    // Determine real-time open/closed status against current clock in store tz
    const now       = new Date(new Date().toLocaleString('en-US', { timeZone: tz }));
    const [oH, oM]  = openStr.split(':').map(Number);
    const [cH, cM]  = closeStr.split(':').map(Number);
    const openMins  = oH * 60 + oM;
    const closeMins = cH * 60 + cM;
    const nowMins   = now.getHours() * 60 + now.getMinutes();
    const currentlyOpen = nowMins >= openMins && nowMins < closeMins;

    return { open: openStr, close: closeStr, isOpen: currentlyOpen };
  }

  // ── Format "HH:MM" → "H:MM AM/PM" ──────────────────────────────────────
  function fmt12(time24) {
    if (!time24) return '';
    const [h, m] = time24.split(':').map(Number);
    const period = h >= 12 ? 'PM' : 'AM';
    const h12    = h % 12 || 12;
    return `${h12}:${String(m).padStart(2, '0')} ${period}`;
  }

  // ── Clone a <template> element's content root ───────────────────────────
  function cloneTemplate(id) {
    return document.getElementById(id).content.firstElementChild.cloneNode(true);
  }

  const SEARCH_QUERY_COOKIE = '_current_search_query';
  const SEARCH_NAME_COOKIE  = '_current_search_name';
  const SEARCH_COOKIE_MAX_AGE = 60 * 60 * 24 * 30;

  function routePath(path) {
    const root = window.Shopify?.routes?.root || '/';
    const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
    return `${normalizedRoot}${String(path).replace(/^\//, '')}`;
  }

  function getCookie(name) {
    const cookie = document.cookie.split('; ').find(item => item.startsWith(`${name}=`));
    if (!cookie) return '';
    try {
      return decodeURIComponent(cookie.slice(name.length + 1));
    } catch (_) {
      return cookie.slice(name.length + 1);
    }
  }

  function setCookie(name, value) {
    document.cookie = `${name}=${encodeURIComponent(value || '')}; path=/; max-age=${SEARCH_COOKIE_MAX_AGE}; SameSite=Lax`;
  }

  function clearCookie(name) {
    document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax`;
  }

  function parseSearchCoordinates(value) {
    if (!value) return null;

    try {
      const parsed = JSON.parse(value);
      const lat = Number(parsed.lat ?? parsed.latitude);
      const lng = Number(parsed.lng ?? parsed.longitude);
      if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
    } catch (_) {}

    const matches = String(value).match(/-?\d+(?:\.\d+)?/g);
    if (!matches || matches.length < 2) return null;

    const lat = Number(matches[0]);
    const lng = Number(matches[1]);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
    return { lat, lng };
  }

  function cityLabelFromSearchName(name) {
    const parts = String(name || '').split(',').map(part => part.trim()).filter(Boolean);
    if (parts.length >= 3) return `${parts[parts.length - 3]}, ${parts[parts.length - 2]}`;
    return parts.join(', ');
  }

  // Google Maps picks its UI language from the `hl` query parameter, falling
  // back to the viewer's browser/account language when it is absent. Shopify
  // gives us `fr`, which Google resolves to France French — so Canadian French
  // has to be requested explicitly as fr-CA. Anything else is passed through
  // unchanged (`en` stays `en`), matching what sections/store-page.liquid
  // already sends on the store detail page.
  const DIRECTIONS_HL = { fr: 'fr-CA' };

  function buildDirectionsUrl(address, locale) {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
    const hl  = DIRECTIONS_HL[locale] || locale || '';
    return hl ? `${url}&hl=${encodeURIComponent(hl)}` : url;
  }

  // The locator uses its stacked mobile/tablet layout below 1024px — the same
  // breakpoint assets/store-locator.css splits its base rules from the desktop
  // overrides on. Evaluated per call rather than cached so a resize or an
  // orientation change is picked up without a reload. Defaults to true where
  // matchMedia is unavailable, which preserves the previous behaviour.
  const DESKTOP_LAYOUT_MQ = '(min-width: 1024px)';
  function isDesktopLayout() {
    return window.matchMedia ? window.matchMedia(DESKTOP_LAYOUT_MQ).matches : true;
  }

  // ── Map custom styles (suppress administrative/POI labels) ───────────────
  const MAP_STYLES = [
    { featureType: 'administrative', elementType: 'labels', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi',            elementType: 'labels', stylers: [{ visibility: 'off' }] }
  ];

  // ── Web Component ────────────────────────────────────────────────────────
  class StoreLocatorMap extends HTMLElement {
    constructor() {
      super();
      this._stores             = [];
      this._markers            = [];
      this._infoWindow         = null;
      this._map                = null;
      this._userPos            = null;
      this._searchCenter       = null;
      this._geoGranted         = false;
      this._geoPermissionDenied = false;
      this._expanded           = false;
      this._activeCard         = null;
      this._lastInRange        = null;
      this._lastCityName       = '';
      this._lastSearchName     = '';
      this._hasRestoredStoredSearch = false;
      this._suppressSearchArea = false;
      this._suppressTimer      = null; // single shared handle — see _suppressMapEvents
      this._searchAreaControl  = null; // the Maps-API-injected control button
      this._pinUrl             = null;
      this._pinSelectedUrl     = null;
      this._pinImage           = null; // preloaded Image element for canvas compositing
      this._userMarker         = null;
      this._searchMarker       = null;
      this._mapControlObserver = null;
    }

    connectedCallback() {
      // Browsers can restore a previously checked radio across reloads and
      // back-forward navigations, independent of the server-rendered `checked`
      // attribute. Force the DOM back in sync with the theme setting before
      // anything else reads it (geolocation init-zoom lookup, _selectedDist, etc.)
      // so a stale restored value can never win. This also guarantees the single
      // -checked invariant _selectedDist depends on, now that the inputs are not
      // a browser radio group and nothing enforces it for us.
      const defaultDist = this.dataset.defaultDistance || '1';
      this._checkDistanceRadio(
        this._distanceRadios.find(radio => radio.value === defaultDist) || this._distanceRadios[0]
      );

      this._pinUrl         = this.dataset.pinUrl;
      this._pinSelectedUrl = this.dataset.pinSelectedUrl;
      this._currentPinUrl  = this.dataset.currentPinUrl || '';
      // data-locale has been on the element since it was written but was never
      // read; it feeds the `hl` parameter on the Get directions links.
      this._locale         = this.dataset.locale || '';
      this._storesReady    = false;
      this._mapReady       = false;

      // Preload the pin PNG so _createPinIcon can composite synchronously.
      // We start this immediately — by the time markers need icons the image
      // will almost certainly be decoded and ready.
      this._pinImage     = new Image();
      this._pinImage.src = this._pinUrl;
      const pinReady     = new Promise(resolve => {
        if (this._pinImage.complete) return resolve();
        this._pinImage.onload  = resolve;
        this._pinImage.onerror = resolve; // degrade gracefully — canvas will still draw the number
      });

      // Preload the selected-state pin so _createPinIcon(n, true) can composite
      // synchronously when a pin is clicked — same pattern as the regular pin above.
      this._pinSelectedImage     = new Image();
      this._pinSelectedImage.src = this._pinSelectedUrl;
      const pinSelectedReady     = new Promise(resolve => {
        if (this._pinSelectedImage.complete) return resolve();
        this._pinSelectedImage.onload  = resolve;
        this._pinSelectedImage.onerror = resolve;
      });

      // Fire store fetch and Maps SDK load concurrently — neither depends on the other
      // until _createMarkers is called. Both set their readiness flag and only the
      // second one to finish will call _createMarkers.
      Promise.all([this._fetchStores(), pinReady, pinSelectedReady]).then(() => {
        this._storesReady = true;
        if (this._mapReady) {
          this._appendMarkers(this._stores);
          if (this._searchCenter) this._refreshResults();
        }
      });

      this._loadMapsScript();
    }

    disconnectedCallback() {
      this._mapControlObserver?.disconnect();
      this._mapControlObserver = null;
    }

    // ── Icon descriptor objects (plain PNG, no rank number) ─────────────────
    // These are used for the hover/deselect state. Rank numbers are composited
    // by _createPinIcon() and applied in _refreshResults via setIcon().
    _iconRegular() {
      return {
        url:        this._pinUrl,
        scaledSize: new google.maps.Size(62, 62),
        origin:     new google.maps.Point(0, 0),
        anchor:     new google.maps.Point(31, 62),
      };
    }

    _iconSelected() {
      return {
        url:        this._pinSelectedUrl,
        scaledSize: new google.maps.Size(62, 62),
        origin:     new google.maps.Point(0, 0),
        anchor:     new google.maps.Point(31, 62),
      };
    }

    // ── Canvas-composited pin icon with rank number ──────────────────────────
    // Draws the rank number directly onto the pin PNG via a canvas, returning a
    // data-URL icon descriptor. This keeps the number strictly inside the 62×62
    // bounding box, preventing Google Maps label text from bleeding over adjacent
    // pins (which happens when using marker.set('label', …) because Maps renders
    // label text outside the icon's own pixel boundary).
    // Pass selected=true to composite onto pin-selected.png instead of the regular
    // pin — this preserves the rank number on the clicked/active pin state.
    _createPinIcon(number, selected = false) {
      const displaySize = 62;
      const scale       = 2;                   // render at 2× for retina sharpness
      const size        = displaySize * scale; // 124px canvas, displayed at 62 CSS px

      const canvas = document.createElement('canvas');
      canvas.width  = size;
      canvas.height = size;
      const ctx    = canvas.getContext('2d');

      // Pick the correct base image: selected state uses pin-selected.png.
      // Guard: if the image hasn't decoded yet, fall back to the plain PNG icon
      // rather than drawing a number onto a blank canvas.
      const img = selected ? this._pinSelectedImage : this._pinImage;
      if (img?.complete && img.naturalWidth > 0) {
        ctx.drawImage(img, 0, 0, size, size);
      } else {
        return this._iconRegular();
      }

      // Composite the rank number onto the pin's circular head.
      // Font size and vertical position scale proportionally with the canvas so
      // the number lands in the same relative position as the 1× version.
      // The head occupies roughly the top 72% of the image; 0.38 × size centres
      // the glyph baseline within that region.
      ctx.fillStyle    = '#000';
      ctx.font         = `600 ${Math.round(16 * scale)}px Calibre, Arial, sans-serif`;
      ctx.textAlign    = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(String(number), size / 2, size * 0.38);

      return {
        url:        canvas.toDataURL(),
        scaledSize: new google.maps.Size(displaySize, displaySize),
        origin:     new google.maps.Point(0, 0),
        anchor:     new google.maps.Point(displaySize / 2, displaySize),
      };
    }

    // ── DOM shortcuts ───────────────────────────────────────────────────────
    get _input()          { return this.querySelector('.store-locator__input'); }
    get _clearBtn()       { return this.querySelector('.store-locator__clear'); }
    get _searchBtn()      { return this.querySelector('.store-locator__search-btn'); }
    get _geoBtn()         { return this.querySelector('.store-locator__geolocate'); }
    get _geoError()       { return this.querySelector('.store-locator__geolocation-error'); }
    get _distanceTrigger(){ return this.querySelector('.store-locator__distance-trigger'); }
    get _distancePanel()  { return this.querySelector('.store-locator__distance'); }
    get _distanceCurrent(){ return this.querySelector('.store-locator__distance-current'); }
    get _distanceRadios() { return Array.from(this.querySelectorAll('.store-locator__distance-radio')); }

    // Radio exclusivity is ours to enforce. The inputs share no `name` so each is
    // its own tab stop (see the comment in sections/store-locator.liquid), which
    // also means the browser no longer unchecks the others for us. Every path
    // that changes the selection goes through here, so exactly one stays checked
    // and _selectedDist — which reads :checked — always resolves.
    //
    // Deliberately does not dispatch `change`: callers that represent a user
    // action fire it themselves.
    _checkDistanceRadio(radio) {
      if (!radio) return;
      this._distanceRadios.forEach(r => { r.checked = r === radio; });
      if (this._distanceCurrent) this._distanceCurrent.textContent = radio.value;
    }

    // Safari can omit buttons and radio inputs from its native Tab sequence,
    // depending on the browser's keyboard-navigation preference. Keep the
    // search controls deterministic from the text field through the distance
    // trigger so keyboard access does not depend on that browser setting.
    _handleSearchControlTab(event, current) {
      if (event.key !== 'Tab') return false;

      const controls = [
        this._input,
        ...(this._clearBtn.hidden ? [] : [this._clearBtn]),
        this._searchBtn,
        this._geoBtn,
        this._distanceTrigger,
      ];
      const index = controls.indexOf(current);
      if (index < 0) return false;

      const targetIndex = index + (event.shiftKey ? -1 : 1);
      const target = controls[targetIndex];
      if (!target) return false;

      event.preventDefault();
      target.focus();
      return true;
    }

    // Safari can also omit radio inputs from its native Tab sequence even when they
    // carry tabindex="0", depending on the browser's keyboard-navigation
    // preference. UAT requires every distance to be reachable with Tab, so the
    // distance trigger explicitly enters the group and intermediate radios move
    // focus themselves. The last radio's forward Tab remains native so focus can
    // leave the group; Shift+Tab from the first returns to the trigger.
    _handleDistanceTab(event, radio = null) {
      if (event.key !== 'Tab' || this._distancePanel?.hidden) return false;

      const radios = this._distanceRadios;
      if (!radios.length) return false;

      let target = null;
      if (!radio) {
        if (event.shiftKey) return false;
        target = radios[0];
      } else {
        const index = radios.indexOf(radio);
        if (index < 0) return false;

        if (event.shiftKey) {
          target = index === 0 ? this._distanceTrigger : radios[index - 1];
        } else if (index < radios.length - 1) {
          target = radios[index + 1];
        }
      }

      if (!target) return false;
      event.preventDefault();
      target.focus();
      return true;
    }

    // Keep the result list's composite cards and their links in a deterministic
    // sequence, then hand focus to See All when it is available. Safari can
    // otherwise move from the last card back to the page header without exposing
    // the CTA. Forward Tab from See All remains native so focus proceeds into
    // the footer instead of being trapped inside the locator.
    _handleResultsTab(event, current) {
      if (event.key !== 'Tab') return false;

      const controls = [];
      this._resultsList.querySelectorAll('.store-location-tile').forEach(card => {
        controls.push(card);
        controls.push(...card.querySelectorAll('a[href], button:not([disabled])'));
      });
      if (!this._seeAllBtn.hidden) controls.push(this._seeAllBtn);

      const index = controls.indexOf(current);
      if (index < 0) return false;

      const target = controls[index + (event.shiftKey ? -1 : 1)];
      if (!target) return false;

      event.preventDefault();
      target.focus();
      return true;
    }
    get _mapToggle()      { return this.querySelector('.store-locator__map-toggle'); }
    get _mapWrapper()     { return this.querySelector('.store-locator__map-wrapper'); }
    get _mapEl()          { return this.querySelector('.store-locator__map'); }
    get _resultsEl()      { return this.querySelector('.store-locator__results'); }
    get _resultsHeader()  { return this.querySelector('.store-locator__results-header'); }
    get _resultsList()    { return this.querySelector('.store-locator__results-list'); }
    get _noResults()      { return this.querySelector('.store-locator__no-results'); }
    get _enterLocation()  { return this.querySelector('.store-locator__enter-location'); }
    get _seeAllBtn()      { return this.querySelector('.store-locator__see-all'); }
    get _selectedDist()   {
      return parseFloat(this.querySelector('.store-locator__distance-radio:checked')?.value || 1);
    }
    get _selectedZoom()   {
      return parseInt(this.querySelector('.store-locator__distance-radio:checked')?.dataset.zoom || String(DETAIL_ZOOM), 10);
    }

    async _fetchStores() {
      const CACHE_KEY  = 'ocs-stores-v7';
      const storesUrl  = this.dataset.storesUrl;

      try {
        const cached = sessionStorage.getItem(CACHE_KEY);
        if (cached) {
          this._stores = JSON.parse(cached).filter(s => s && s.latitude != null);
          return;
        }
      } catch (_) {}

      if (!storesUrl) {
        console.warn('[store-locator] data-stores-url not set — select the store data page in theme settings');
        this._stores = [];
        return;
      }

      try {
        const seenIds = new Set();

        const parsePage = async (page, param = 'page') => {
          const url = page === 1 ? storesUrl : `${storesUrl}?${param}=${page}`;
          const res = await fetch(url);
          return res.json();
        };

        const dedup = (stores) => (stores || []).filter(s => {
          if (!s || s.latitude == null) return false;
          if (seenIds.has(s.id)) return false;
          seenIds.add(s.id);
          return true;
        });

        const first      = await parsePage(1);
        const pageParam  = first.page_param || 'page';
        const totalPages = first.pages || 1;
        this._stores     = dedup(first.stores);

        if (totalPages > 1) {
          (async () => {
            try {
              for (let page = 2; page <= totalPages; page++) {
                const data  = await parsePage(page, pageParam);
                const valid = dedup(data.stores);
                if (valid.length === 0) break;
                this._stores = this._stores.concat(valid);
                if (this._map) this._appendMarkers(valid);
              }
            } catch (_) {}
            try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(this._stores)); } catch (_) {}
          })();
        } else {
          try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(this._stores)); } catch (_) {}
        }
      } catch (e) {
        console.error('[store-locator] Failed to load store data', e);
        this._stores = [];
      }
    }

    // ── Load Google Maps SDK ─────────────────────────────────────────────────
    _loadMapsScript() {
      if (window.google?.maps) {
        this._initMap();
        this._mapReady = true;
        if (this._storesReady) {
          this._appendMarkers(this._stores);
          if (this._searchCenter) this._refreshResults();
        }
        return;
      }
      window.initStoreLocator = () => {
        this._initMap();
        this._mapReady = true;
        if (this._storesReady) {
          this._appendMarkers(this._stores);
          if (this._searchCenter) this._refreshResults();
        }
      };
      const script = document.createElement('script');
      const apiKey = this.dataset.apiKey;
      const language = String(this._locale || document.documentElement.lang || 'en').toLowerCase().startsWith('fr') ? 'fr' : 'en';
      script.src   = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&language=${language}&region=CA&callback=initStoreLocator&loading=async`;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    // ── Initialize map ───────────────────────────────────────────────────────
    _initMap() {
      this._map = new google.maps.Map(this._mapEl, {
        center:           DEFAULT_CENTER,
        zoom:             DEFAULT_ZOOM,
        styles:           MAP_STYLES,
        // Match production: suppress all default controls (including the new
        // Camera Control) and re-enable only the traditional +/- zoom buttons.
        disableDefaultUI: true,
        zoomControl:      true,
        scrollwheel:      false,
      });

      this._infoWindow = new google.maps.InfoWindow();
      this._observeMapKeyboardShortcutControls();

      // _createMarkers() is called separately once BOTH _mapReady and _storesReady
      // are true — see connectedCallback and _loadMapsScript.
      this._setSearchThisArea();
      this._bindEvents();
    }

    // ── Suppress the "user moved the map" listeners around a programmatic move ──
    // Every programmatic setCenter/setZoom/panTo/fitBounds must be wrapped in this,
    // or the zoom_changed/idle listeners in _setSearchThisArea mistake it for a pan
    // and re-filter the list by viewport instead of by the selected distance.
    //
    // The timer handle is shared and cleared on every call, so the LAST suppression
    // always wins. That matters because these calls nest: the geolocate button and
    // the search paths move the map and then call _refreshResults(), whose fitBounds
    // suppresses again. With a per-call timer the outer (earlier, shorter) one would
    // fire first and lower the flag while fitBounds was still animating — the map's
    // own zoom_changed would then read as user input and widen the results past the
    // selected radius.
    //
    // 300ms matches the idle debounce below; the Maps API settles well inside it.
    _suppressMapEvents(ms = 300) {
      this._suppressSearchArea = true;
      clearTimeout(this._suppressTimer);
      this._suppressTimer = setTimeout(() => {
        this._suppressSearchArea = false;
        this._suppressTimer      = null;
      }, ms);
    }

    // ── Auto-refresh results when the user moves the map ────────────────────
    // Only fires on user-initiated movement (drag or unprogrammatic zoom).
    // Programmatic moves (fitBounds, panTo, setZoom) call _suppressMapEvents()
    // before they run, so zoom_changed won't set _userMoved and idle is skipped.
    // This prevents the post-search fitBounds from overwriting the distance filter.
    _setSearchThisArea() {
      let _idleTimer = null;
      let _userMoved = false;

      // Drag is always user-initiated
      this._map.addListener('dragstart', () => {
        _userMoved = true;
      });

      // Zoom via scroll/pinch: fire only when not suppressed by programmatic zoom
      this._map.addListener('zoom_changed', () => {
        if (!this._suppressSearchArea) _userMoved = true;
      });

      this._map.addListener('idle', () => {
        this._dedupeMapKeyboardShortcutControls();
        if (!_userMoved) return;
        _userMoved = false;
        clearTimeout(_idleTimer);
        _idleTimer = setTimeout(() => {
          this._expanded = false;
          this._refreshResults({ fitMap: false, useBounds: true });
        }, 300);
      });
    }

    // ── Clear all markers from map and reset the markers array ─────────────
    _clearMarkers() {
      this._markers.forEach(m => m.setMap(null));
      this._markers = [];
    }

    // ── Append markers for a given array of stores ───────────────────────────
    // Only ADDS markers — never clears. Call _clearMarkers() first when a full
    // reset is needed (e.g. a future re-index). Accepts a stores array so it can
    // be called incrementally as background pages arrive.
    _appendMarkers(stores) {
      stores.forEach((store) => {
        if (!store || store.latitude == null) return;
        // Derive global index from the canonical this._stores array so that
        // _highlightCard and _refreshResults can look up markers by position.
        const idx = this._stores.indexOf(store);
        const marker = new google.maps.Marker({
          // lat/lng from JSON can arrive as strings — always coerce
          position:  { lat: parseFloat(store.latitude), lng: parseFloat(store.longitude) },
          map:       null, // not attached to the map yet — setMap(this._map) in _refreshResults
          title:     store.title,
          icon:      this._iconRegular(),
          optimized: false,
        });
        marker._storeTitle = store.title;

        marker.addListener('click', () => {
          // Switch to selected icon — composite the rank number over pin-selected.png
          // so the number stays visible on the clicked pin (not just the plain selected PNG).
          marker.setIcon(this._createPinIcon(marker._rank || 1, true));
          // Prefer the inRange copy of this store (which carries _originalIdx and
          // _distKm) so _openInfoWindow can resolve rank and build the detail URL
          // correctly even when store.id is null (metaobject-sourced stores).
          const storeForWindow = this._lastInRange?.find(s => s._originalIdx === idx) || store;
          this._openInfoWindow(storeForWindow, marker);
          // If the clicked store's card is collapsed (rank ≥ MAX_CARDS),
          // expand the list first so _highlightCard can find it.
          if (this._lastInRange) {
            const rankInRange = storeForWindow._originalIdx != null
              ? this._lastInRange.findIndex(s => s._originalIdx === storeForWindow._originalIdx)
              : this._lastInRange.findIndex(s => s.id === store.id);
            if (rankInRange >= MAX_CARDS && !this._expanded) {
              this._expanded = true;
              this._seeAllBtn.hidden = true;
              this._renderResultsList(this._lastInRange, true);
            }
          }
          this._highlightCard(idx);
        });

        // Reset to numbered icon when the infoWindow is closed so the rank number
        // is preserved on the pin. Fall back to plain PNG only if no numbered icon
        // has been assigned yet (e.g. marker not yet ranked by _refreshResults).
        this._infoWindow.addListener('closeclick', () => {
          marker.setIcon(marker._numberedIcon || this._iconRegular());
          marker.set('label', null);
        });

        this._markers.push(marker);
      });

    }

    _toggleMobileMap() {
      if (!this._mapToggle || !this._mapWrapper) return;

      const isHidden = this._mapWrapper.classList.toggle('store-locator__map-wrapper--mobile-hidden');
      this._mapToggle.setAttribute('aria-expanded', String(!isHidden));
      this._mapToggle.textContent = isHidden ? this._mapToggle.dataset.showText : this._mapToggle.dataset.hideText;

      if (!isHidden && this._map) {
        const center = this._map.getCenter?.();
        requestAnimationFrame(() => {
          if (window.google?.maps?.event?.trigger) google.maps.event.trigger(this._map, 'resize');
          if (center) this._map.setCenter(center);
        });
      }
    }

    // A search cannot run without a location, and both search paths used to do
    // nothing whatsoever when the field was empty: the magnifier because
    // _selectFirstAutocompleteResult() returns on an empty query, Enter because
    // of its own guard below.
    //
    // The field is marked `required` (see sections/store-locator.liquid), but
    // nothing was asking the browser to act on that. Constraint validation only
    // reports itself as part of submitting a form, and this input deliberately
    // has no form around it — so the field was correctly invalid and silently
    // so. Apply the Liquid-rendered message before reportValidity() so the
    // bubble follows the storefront locale rather than the browser/OS language.
    //
    // Returns true when it has reported, so callers can stop — and they must
    // stop rather than carry on, because reportValidity() focuses the field to
    // anchor the bubble and the blur() that ends a normal search would dismiss
    // it again immediately.
    _reportMissingLocation() {
      if (this._input.value.trim()) return false;
      this._input.setCustomValidity(this._input.dataset.requiredMessage || '');
      this._input.reportValidity();
      return true;
    }

    // ── Bind UI events ───────────────────────────────────────────────────────
    _bindEvents() {
      if (this._mapToggle) {
        this._mapToggle.addEventListener('click', () => this._toggleMobileMap());
      }

      // Input clear button visibility
      this._input.addEventListener('input', () => {
        this._input.setCustomValidity('');
        this._clearBtn.hidden = this._input.value.trim() === '';
      });
      this._input.addEventListener('keydown', (event) => {
        this._handleSearchControlTab(event, this._input);
      });

      // Clear button
      this._clearBtn.addEventListener('click', () => {
        this._input.value     = '';
        this._lastSearchName  = '';
        clearCookie(SEARCH_QUERY_COOKIE);
        clearCookie(SEARCH_NAME_COOKIE);
        this._clearBtn.hidden = true;
        this._input.focus();
        // Preserve last results — do not wipe the list
      });
      this._clearBtn.addEventListener('keydown', (event) => {
        this._handleSearchControlTab(event, this._clearBtn);
      });

      // The magnifier runs the same path as Enter — select the top autocomplete
      // prediction. It previously called _searchTypedLocation(), which resolves
      // the raw typed string through the Geocoder instead; that is a different
      // service with different ranking, so the button and the Enter key could
      // land on different places for identical input, and the results heading
      // came from parsing the typed text rather than from real address
      // components. _selectFirstAutocompleteResult() falls back to
      // _searchTypedLocation() on its own when Places is unavailable or returns
      // no predictions, so the geocoder path is still reachable.
      //
      // Unlike the Enter handler below, this does not defer to a suggestion the
      // user has arrowed to: the button always takes the first result. Pressing
      // the mouse down on the button blurs the field and closes the prediction
      // panel before click fires, so there is no reliable arrowed selection left
      // to honour by that point.
      this._searchBtn.addEventListener('click', () => {
        if (this._reportMissingLocation()) return;
        this._selectFirstAutocompleteResult();
        // Match the Enter path and make sure the prediction panel is dismissed.
        // No-op when the click already moved focus off the field.
        this._input.blur();
      });
      this._searchBtn.addEventListener('keydown', (event) => {
        this._handleSearchControlTab(event, this._searchBtn);
      });

      // Enter in the search field selects the first autocomplete suggestion.
      // Captured on the host element (which contains the input) so this runs
      // before Google's own input keydown listener, letting us read the dropdown
      // state and suppress the widget's empty-geometry place_changed on raw text.
      // If the user has arrowed to a specific suggestion, we defer to the widget.
      this.addEventListener('keydown', (e) => {
        if (e.target !== this._input || e.key !== 'Enter') return;
        // Reports the empty field and returns, as before, without calling
        // preventDefault — the widget keeps its own Enter handling, which has
        // nothing to act on with no query and so cannot dismiss the bubble.
        if (this._reportMissingLocation()) return;
        const pac = document.querySelector('.pac-container');
        if (pac && pac.querySelector('.pac-item-selected')) return; // let the widget select it
        e.preventDefault();
        e.stopPropagation();
        this._selectFirstAutocompleteResult();
        // We suppressed the widget's own Enter handling above, so it won't close
        // the prediction panel — blur the field to dismiss it, as a click-select would.
        this._input.blur();
      }, true);

      // Google Maps gives its markers one shared Tab stop and expects arrow keys
      // to move within that group. UAT requires each visible location to remain
      // in the page's Tab sequence, so bridge Tab/Shift+Tab between marker nodes.
      // Capture runs before Google's marker listener; the first/last boundary is
      // left alone so focus can enter and leave the map normally.
      this._mapEl.addEventListener('keydown', (event) => {
        this._handleMapMarkerTab(event);
      }, true);

      // Geolocate button
      this._geoBtn.addEventListener('click', () => {
        if (this._geoGranted && this._userPos) {
          // Clear the typed-search pin — reverting to GPS location supersedes it
          if (this._searchMarker) { this._searchMarker.setMap(null); this._searchMarker = null; }
          this._searchCenter = this._userPos;
          this._suppressMapEvents();
          this._map.setCenter(this._userPos);
          this._map.setZoom(this._selectedZoom);
          this._showUserLocation(this._userPos);
          this._refreshResults();
          return;
        }
        // A denied browser permission cannot prompt again until site settings
        // change. Give the click an immediate visible result, then retry in case
        // permission was changed in a browser without Permissions API support.
        if (this._geoPermissionDenied) this._showGeolocationError();
        // recenter: true so a stored search restored on load does not suppress
        // the map move the user just asked for.
        this._requestGeolocation({ recenter: true });
      });
      this._geoBtn.addEventListener('keydown', (event) => {
        this._handleSearchControlTab(event, this._geoBtn);
      });

      // Distance trigger toggle
      this._distanceTrigger.addEventListener('click', () => {
        const open = this._distanceTrigger.getAttribute('aria-expanded') === 'true';
        this._distanceTrigger.setAttribute('aria-expanded', String(!open));
        this._distancePanel.hidden = open;
      });

      // Escape key closes the distance panel
      this._distanceTrigger.addEventListener('keydown', (event) => {
        if (this._handleSearchControlTab(event, this._distanceTrigger)) return;
        if (this._handleDistanceTab(event)) return;
        if (event.key === 'Escape') {
          this._distanceTrigger.setAttribute('aria-expanded', 'false');
          this._distancePanel.hidden = true;
          this._distanceTrigger.focus();
        }
      });

      // Distance radio change — also sets zoom level from data-zoom attribute
      this._distanceRadios.forEach(radio => {
        // Arrow keys used to move and select for free, because the inputs were a
        // browser radio group. They are not any more (each is its own tab stop),
        // so reproduce it here — role="radiogroup" is on the list, and that role
        // promises arrow-key navigation.
        radio.addEventListener('keydown', (e) => {
          if (this._handleDistanceTab(e, radio)) return;

          const forward = e.key === 'ArrowRight' || e.key === 'ArrowDown';
          const back    = e.key === 'ArrowLeft'  || e.key === 'ArrowUp';
          if (!forward && !back) return;
          e.preventDefault();

          const all  = this._distanceRadios;
          const next = all[(all.indexOf(radio) + (forward ? 1 : -1) + all.length) % all.length];
          if (!next || next === radio) return;

          this._checkDistanceRadio(next);
          next.focus();
          // Setting .checked in script fires no change event, and this one is a
          // real user action, so dispatch it to reach the handler below.
          next.dispatchEvent(new Event('change', { bubbles: true }));
        });

        radio.addEventListener('change', () => {
          // Update the selection and the trigger label regardless
          this._checkDistanceRadio(radio);

          // No search center yet (no geolocation and no search) — nothing to filter/zoom around
          if (!this._searchCenter) return;

          const zoom = parseInt(radio.dataset.zoom || '10', 10);
          this._expanded           = false;
          this._seeAllBtn.hidden   = true;
          // setZoom + panTo: distance change must control the zoom level directly.
          // _refreshResults is told to skip fitBounds so it doesn't override this zoom.
          this._suppressMapEvents();
          this._map.setZoom(zoom);
          this._map.panTo(this._searchCenter);
          this._refreshResults({ fitMap: false });
        });
      });

      // See all stores
      this._seeAllBtn.addEventListener('click', () => {
        this._expanded         = true;
        this._seeAllBtn.hidden = true;
        this._renderResultsList(this._lastInRange, true);
        // Move focus to the first newly revealed card (index 3) so keyboard users
        // can continue tabbing forward through the expanded list without losing
        // their place. The <li> tiles have tabindex="0" set in _renderResultsList.
        const cards = this._resultsList.querySelectorAll('.store-location-tile');
        if (cards[3]) cards[3].focus({ preventScroll: true });
      });
      this._seeAllBtn.addEventListener('keydown', (event) => {
        this._handleResultsTab(event, this._seeAllBtn);
      });

      // Google Places autocomplete — restricted to Canada. No `types` filter so
      // suggestions include establishments/landmarks (e.g. "CN Tower") as well as
      // addresses. The classic Autocomplete API rejects combining 'geocode' with
      // 'establishment', so omitting `types` is the only way to return both.
      this._autocomplete = new google.maps.places.Autocomplete(this._input, {
        componentRestrictions: { country: 'CA' },
      });
      // Bias suggestions toward the current map viewport
      this._autocomplete.bindTo('bounds', this._map);

      this._autocomplete.addListener('place_changed', () => {
        const place = this._autocomplete.getPlace();
        if (!place.geometry?.location) return;
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();

        // Extract city name from the Places result so the results heading reads
        // "Authorized Stores Near Toronto, ON" instead of just "Authorized Stores Near".
        // Combine locality (city) + administrative_area_level_1 short name (province)
        // so the label is unambiguous for common city names (e.g. "London, ON").
        const comps    = place.address_components || [];
        const locality = comps.find(c => c.types.includes('locality') || c.types.includes('postal_town'));
        const region   = comps.find(c => c.types.includes('administrative_area_level_1'));
        this._lastCityName = [locality?.long_name, region?.short_name].filter(Boolean).join(', ') || place.name || '';
        this._lastSearchName = this._input.value.trim() || place.formatted_address || place.name || this._lastCityName;

        this._searchCenter = { lat, lng };
        this._hideGeolocationError();
        this._showSearchLocation({ lat, lng });
        this._suppressMapEvents();
        this._map.setCenter({ lat, lng });
        this._map.setZoom(this._selectedZoom);
        this._storeSearch(this._searchCenter, this._lastSearchName);
        this._expanded         = false;
        this._seeAllBtn.hidden = true;
        this._hideSearchAreaControl();
        this._refreshResults();
        this._clearBtn.hidden = this._input.value.trim() === '';
      });

      this._restoreStoredSearch();
    }

    // ── Hide the Maps-API search-area control ────────────────────────────────
    _hideSearchAreaControl() {
      if (!this._searchAreaControl) return;
      this._searchAreaControl.style.transform = 'translateY(-100px)';
      this._searchAreaControl.setAttribute('tabindex', '-1');
    }

    _storeSearch(center, name) {
      if (!center) return;
      setCookie(SEARCH_QUERY_COOKIE, `${center.lat},${center.lng}`);
      setCookie(SEARCH_NAME_COOKIE, name || '');
    }

    // Resolve a typed query to { center, name } via the Geocoder — handles
    // addresses, postal codes, and city names. Returns null on no match.
    async _resolveViaGeocoder(query) {
      if (!query || !window.google?.maps?.Geocoder) return null;
      try {
        const geocoder = new google.maps.Geocoder();
        const { results } = await geocoder.geocode({
          address: query,
          componentRestrictions: { country: 'CA' },
        });
        const location = results?.[0]?.geometry?.location;
        if (!location) return null;
        return { center: { lat: location.lat(), lng: location.lng() }, name: query };
      } catch (_) {
        return null;
      }
    }

    // Resolve a typed query to { center, name } via Places Find Place — this is
    // what lets landmark / business names (e.g. "CN Tower") resolve, since the
    // Geocoder only understands addresses. Biased to the current viewport so
    // ambiguous names resolve locally. Requires the Places API on the key;
    // resolves null (rather than throwing) if Places is unavailable/denied.
    _resolveViaPlaces(query) {
      return new Promise((resolve) => {
        if (!query || !window.google?.maps?.places?.PlacesService) { resolve(null); return; }
        try {
          this._placesService = this._placesService || new google.maps.places.PlacesService(this._map);
          const request = { query, fields: ['geometry', 'name', 'formatted_address'] };
          const bounds = this._map?.getBounds?.();
          if (bounds) request.locationBias = bounds;
          this._placesService.findPlaceFromQuery(request, (results, status) => {
            const ok = status === google.maps.places.PlacesServiceStatus.OK;
            const location = ok ? results?.[0]?.geometry?.location : null;
            if (!location) { resolve(null); return; }
            resolve({ center: { lat: location.lat(), lng: location.lng() }, name: results[0].name || query });
          });
        } catch (_) {
          resolve(null);
        }
      });
    }

    async _searchTypedLocation() {
      const query = this._input?.value?.trim();
      if (!query) return;

      // Geocoder first (unchanged behavior for addresses / postal codes / cities),
      // then fall back to Places so landmark and business names also resolve.
      const resolved = (await this._resolveViaGeocoder(query)) || (await this._resolveViaPlaces(query));
      if (!resolved) return;

      this._applyResolvedSearch(resolved.center, query, cityLabelFromSearchName(query) || query);
    }

    // Shared "centre the map on a resolved location and refresh the list" tail,
    // used by typed search and the Enter-selects-first-suggestion path.
    _applyResolvedSearch(center, searchName, cityName) {
      this._lastSearchName = searchName;
      this._lastCityName   = cityName || cityLabelFromSearchName(searchName) || searchName;
      this._searchCenter   = center;
      this._hideGeolocationError();
      this._showSearchLocation(center);
      this._suppressMapEvents();
      this._map.setCenter(center);
      this._map.setZoom(this._selectedZoom);
      this._expanded         = false;
      this._seeAllBtn.hidden = true;
      this._hideSearchAreaControl();
      this._storeSearch(center, this._lastSearchName);
      this._refreshResults();
      if (this._clearBtn) this._clearBtn.hidden = this._input.value.trim() === '';
    }

    // Pressing Enter without arrowing to a suggestion: pick the top autocomplete
    // prediction, as if the user had clicked the first row of the dropdown. Fetch
    // predictions fresh via AutocompleteService (independent of the dropdown DOM),
    // then resolve the winning place_id to coordinates. Falls back to a plain
    // typed search when no predictions are available (e.g. Places disabled).
    _selectFirstAutocompleteResult() {
      const query = this._input?.value?.trim();
      if (!query) return;
      if (!window.google?.maps?.places?.AutocompleteService) {
        this._searchTypedLocation();
        return;
      }
      this._autocompleteService = this._autocompleteService || new google.maps.places.AutocompleteService();
      const request = { input: query, componentRestrictions: { country: 'ca' } };
      const bounds = this._map?.getBounds?.();
      if (bounds) request.bounds = bounds;
      this._autocompleteService.getPlacePredictions(request, (predictions, status) => {
        const ok = status === google.maps.places.PlacesServiceStatus.OK;
        const top = ok ? predictions?.[0] : null;
        if (!top?.place_id) { this._searchTypedLocation(); return; }
        // Reflect the chosen prediction in the field, mirroring a click-select.
        if (top.description) this._input.value = top.description;
        this._resolvePlaceId(top.place_id, this._input.value.trim() || query);
      });
    }

    // Resolve a place_id to coordinates + a city label via Places getDetails —
    // the SAME geometry source the click/place_changed path uses (getPlace()), so
    // pressing Enter lands identically to clicking the first suggestion (rather
    // than centring on the Geocoder's differing point for the same place). Falls
    // back to a typed search if the lookup fails.
    _resolvePlaceId(placeId, searchName) {
      if (!window.google?.maps?.places?.PlacesService) { this._searchTypedLocation(); return; }
      this._placesService = this._placesService || new google.maps.places.PlacesService(this._map);
      this._placesService.getDetails(
        { placeId, fields: ['geometry', 'address_components', 'name'] },
        (place, status) => {
          const location = status === google.maps.places.PlacesServiceStatus.OK
            ? place?.geometry?.location
            : null;
          if (!location) { this._searchTypedLocation(); return; }
          const center   = { lat: location.lat(), lng: location.lng() };
          const comps    = place.address_components || [];
          const locality = comps.find(c => c.types.includes('locality') || c.types.includes('postal_town'));
          const region   = comps.find(c => c.types.includes('administrative_area_level_1'));
          const cityName = [locality?.long_name, region?.short_name].filter(Boolean).join(', ');
          this._applyResolvedSearch(center, searchName, cityName || cityLabelFromSearchName(searchName) || searchName);
        }
      );
    }

    async _restoreStoredSearch() {
      const query = getCookie(SEARCH_QUERY_COOKIE);
      const name  = getCookie(SEARCH_NAME_COOKIE);
      if (!query && !name) return false;

      let center = parseSearchCoordinates(query);
      if (!center) {
        const address = query || name;
        if (!address) return false;
        // Same geocoder-then-Places resolution as a fresh typed search, so a
        // stored landmark search restores correctly on the next page load too.
        const resolved = (await this._resolveViaGeocoder(address)) || (await this._resolveViaPlaces(address));
        if (!resolved) return false;
        center = resolved.center;
      }

      this._hasRestoredStoredSearch = true;
      this._lastSearchName = name || query || '';
      this._lastCityName = cityLabelFromSearchName(this._lastSearchName) || this._lastSearchName;
      this._searchCenter = center;

      if (this._input && this._lastSearchName) this._input.value = this._lastSearchName;
      if (this._clearBtn) this._clearBtn.hidden = !this._lastSearchName;

      this._showSearchLocation(center);
      this._suppressMapEvents();
      this._map.setCenter(center);
      this._map.setZoom(this._selectedZoom);
      this._expanded = false;
      this._seeAllBtn.hidden = true;
      this._hideSearchAreaControl();
      this._storeSearch(center, this._lastSearchName);

      if (this._storesReady) this._refreshResults();
      return true;
    }

    // ── Geolocation request ─────────────────────────────────────────────────

    // The geolocate button starts active so the permission request originates
    // from its user-initiated click. Disable it only when the Geolocation API is
    // unavailable; after denial it stays active for retries while the associated
    // guidance explains the manual-search alternative.
    _setGeoAvailable(available) {
      if (this._geoBtn) this._geoBtn.disabled = !available;
    }

    // Permission can change after load — the user can grant or revoke it from
    // the browser's site controls without reloading. Reflect that on the button
    // rather than leaving it stuck in whatever state page load happened to find.
    // navigator.permissions is absent on some older Safari builds, and querying
    // 'geolocation' throws rather than rejecting on a few others, hence both
    // guards.
    _watchGeolocationPermission() {
      if (this._geoPermissionWatched || !navigator.permissions?.query) return;
      this._geoPermissionWatched = true;
      try {
        navigator.permissions.query({ name: 'geolocation' }).then((status) => {
          status.addEventListener('change', () => {
            if (status.state === 'granted') {
              this._geoPermissionDenied = false;
              this._hideGeolocationError();
              this._requestGeolocation();
            } else {
              this._geoGranted          = false;
              this._userPos             = null;
              this._geoPermissionDenied = status.state === 'denied';
              this._setGeoAvailable(true);
              if (this._geoPermissionDenied && !this._hasRestoredStoredSearch) {
                this._showEnterLocationHelper();
              }
            }
          });
        }).catch(() => {});
      } catch (_) {}
    }

    // recenter: true when the user explicitly asked for their location by
    // clicking the button, which overrides the restored-search early return
    // below — an explicit click should move the map even if a stored search was
    // rehydrated on load.
    _requestGeolocation({ recenter = false } = {}) {
      if (!navigator.geolocation) {
        this._setGeoAvailable(false);
        this._showEnterLocationHelper();
        return;
      }
      this._watchGeolocationPermission();
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          this._geoGranted          = true;
          this._geoPermissionDenied = false;
          this._userPos             = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          this._hideGeolocationError();
          this._setGeoAvailable(true);
          if (this._map) this._showUserLocation(this._userPos);

          if (this._hasRestoredStoredSearch && !recenter) return;

          // Clear the typed-search pin — geolocation supersedes it
          if (this._searchMarker) { this._searchMarker.setMap(null); this._searchMarker = null; }
          this._searchCenter = this._userPos;
          // No reverse-geocoding for geolocation — clear city name so the heading
          // reads "Authorized Stores Near" rather than showing a stale typed city.
          this._lastCityName = '';
          this._suppressMapEvents();
          this._map.setCenter(this._userPos);
          this._map.setZoom(this._selectedZoom);
          this._refreshResults();
        },
        (err) => {
          this._geoGranted          = false;
          this._geoPermissionDenied = err?.code === 1;
          this._userPos             = null;
          // Keep the control active after every browser response. A denied
          // request cannot prompt again until the user changes site settings,
          // so an explicit click also reveals browser-settings guidance.
          // POSITION_UNAVAILABLE (2) and TIMEOUT (3) remain retryable.
          this._setGeoAvailable(true);
          if (this._geoPermissionDenied && recenter) this._showGeolocationError();
          if (!this._hasRestoredStoredSearch) this._showEnterLocationHelper();
        },
        // Fail fast and reuse a recent fix instead of forcing a fresh (often
        // failing on desktop) CoreLocation lookup: accept a cached position up
        // to 10 min old, cap the wait at 8s, and skip high-accuracy GPS.
        {
          enableHighAccuracy: false,
          timeout: 8000,
          maximumAge: 600000,
        }
      );
    }

    _showEnterLocationHelper() {
      this._resultsEl.hidden     = true;
      this._enterLocation.hidden = false;
      this._noResults.hidden     = true;
    }

    _showGeolocationError() {
      if (this._geoError) this._geoError.hidden = false;
    }

    _hideGeolocationError() {
      if (this._geoError) this._geoError.hidden = true;
    }

    // ── Current location pin ────────────────────────────────────────────────
    // Uses the same green pin asset as searched/typed locations.
    _showUserLocation(pos) {
      const icon = {
        url:        this._currentPinUrl,
        scaledSize: new google.maps.Size(44, 44),
        anchor:     new google.maps.Point(22, 44),
      };

      if (this._userMarker) {
        this._userMarker.setPosition(pos);
        this._userMarker.setIcon(icon);
        this._userMarker.setMap(this._map);
      } else {
        this._userMarker = new google.maps.Marker({
          position:  pos,
          map:       this._map,
          title:     window.themeStrings?.yourLocation || 'Your location',
          icon,
          zIndex:    999,
          optimized: false,
        });
      }
    }

    // ── Custom pin at the searched/typed location ────────────────────────────
    // Uses the same pin-current.png asset as _showUserLocation.
    _showSearchLocation(pos) {
      if (this._searchMarker) { this._searchMarker.setPosition(pos); return; }
      this._searchMarker = new google.maps.Marker({
        position:  pos,
        map:       this._map,
        title:     window.themeStrings?.searchedLocation || 'Searched location',
        icon: {
          url:        this._currentPinUrl,
          scaledSize: new google.maps.Size(44, 44),
          anchor:     new google.maps.Point(22, 44),
        },
        animation: google.maps.Animation.DROP,
        zIndex:    998,
        optimized: false,
      });
    }

    // Manual searches use the selected distance as a strict radius. Never append
    // farther stores while leaving (for example) 1 km selected: that makes the UI
    // look as though it changed the distance without updating the control.
    _selectRadiusResults(withDist, dist) {
      const within = withDist.filter(store => store._distKm <= dist);
      return { results: within, areaMatchCount: within.length };
    }

    // ── Refresh results for current center + distance ────────────────────────
    _refreshResults({ fitMap = true, useBounds = false } = {}) {
      if (!this._searchCenter) return;

      this._lastUseBounds = useBounds;

      const dist   = this._selectedDist;
      const center = this._searchCenter;

      // Map every store to its distance once, then sort ascending.
      // _originalIdx preserves the position in this._stores so marker lookups
      // never depend on store.id — which is null for metaobject-sourced stores.
      const withDist = this._stores.map((s, i) => ({
        ...s,
        _distKm: haversine(center.lat, center.lng, parseFloat(s.latitude), parseFloat(s.longitude)),
        _originalIdx: i,
      })).sort((a, b) => a._distKm - b._distKm);


      let inRange;
      if (useBounds && this._map?.getBounds()) {
        // On map pan/zoom: preserve the existing viewport behavior. #1863 tracks
        // replacing this automatic refresh with an explicit Search this area CTA.
        const bounds = this._map.getBounds();
        inRange = withDist.filter(s =>
          bounds.contains(new google.maps.LatLng(parseFloat(s.latitude), parseFloat(s.longitude)))
        );
      } else {
        inRange = this._selectRadiusResults(withDist, dist).results;
      }

      this._lastInRange = inRange;

      // Remove all markers from the map — only in-range markers will be re-attached.
      // setMap(null) vs setVisible(false): setMap detaches the DOM node entirely so
      // the renderer never has to reposition off-screen markers during pan/zoom.
      this._markers.forEach(marker => marker.setMap(null));

      // Show in-range markers with rank label rendered over the PNG.
      // Use _originalIdx directly — findIndex(s => s.id === ...) is unreliable
      // when store.id is null (all metaobject stores share null, so findIndex
      // always returns 0, overwriting marker[0]'s label on every iteration).
      inRange.forEach((store, rank) => {
        const idx = store._originalIdx;
        if (idx == null || !this._markers[idx]) return;
        // Use canvas-composited icon so the number is clipped to the 62×62 frame
        // and cannot bleed over adjacent pins the way a Maps label would.
        const numberedIcon = this._createPinIcon(rank + 1);
        // Persist both the rank and the numbered icon on the marker so that:
        //   _rank        — lets _createPinIcon(marker._rank, true) render the correct
        //                  number over pin-selected.png when the pin is clicked.
        //   _numberedIcon — lets deselect paths restore the regular numbered icon
        //                  without falling back to the plain unnumbered PNG.
        this._markers[idx]._rank         = rank + 1;
        this._markers[idx]._numberedIcon = numberedIcon;
        this._markers[idx].setIcon(numberedIcon);
        this._markers[idx].set('label', null);
        this._markers[idx].setMap(this._map);
      });

      // On geolocation / search-area: fit map to show all in-range stores.
      // On distance change (fitMap=false): zoom+pan already set by the caller — skip.
      if (fitMap && inRange.length > 0) {
        // Build a bounds that is SYMMETRIC around the searched point: for each
        // in-range store we extend with the store itself *and* its mirror across
        // the search center. This keeps the search point at the exact center of
        // the viewport (even when we zoom out to include far stores) instead of
        // letting fitBounds recenter on the centroid of {point + stores}.
        const bounds = new google.maps.LatLngBounds();
        bounds.extend({ lat: center.lat, lng: center.lng });
        inRange.forEach(s => {
          const lat = parseFloat(s.latitude);
          const lng = parseFloat(s.longitude);
          bounds.extend({ lat, lng });
          bounds.extend({ lat: 2 * center.lat - lat, lng: 2 * center.lng - lng });
        });
        this._suppressMapEvents();
        this._map.fitBounds(bounds);
      }

      this._renderResults(inRange, this._lastCityName);
    }

    // ── Render results panel ─────────────────────────────────────────────────
    _renderResults(inRange, cityName) {
      this._enterLocation.hidden = true;
      this._noResults.hidden     = true;

      if (inRange.length === 0) {
        this._resultsList.innerHTML = '';
        this._resultsEl.hidden      = true;
        this._noResults.hidden      = false;
        this._seeAllBtn.hidden      = true;
        return;
      }

      const I      = window.themeStrings || {};
      const place  = cityName || I.yourArea || 'your area';
      this._resultsHeader.textContent = (I.locationsNear || 'Authorized stores near %%PLACE%%')
        .replace('%%PLACE%%', place);
      this._resultsEl.hidden = false;

      this._renderResultsList(inRange, this._expanded);

      this._seeAllBtn.hidden = !(inRange.length > MAX_CARDS && !this._expanded);

      // Announce result count to screen readers via the dedicated aria-live span.
      // Clear first so a repeat search with the same count still fires the event.
      const announceEl = this.querySelector('.store-locator__sr-announce');
      if (announceEl) {
        announceEl.textContent = '';
        requestAnimationFrame(() => {
          announceEl.textContent = `${inRange.length} stores found`;
        });
      }
    }

    _renderResultsList(inRange, showAll) {
      const displayList = showAll ? inRange : inRange.slice(0, MAX_CARDS);
      this._resultsList.innerHTML = '';

      const renderTile = (store, rank) => {
        // _originalIdx is stamped in _refreshResults; fall back to identity
        // comparison only if somehow missing (e.g. a store added before the fix).
        const globalIdx = store._originalIdx ?? this._stores.findIndex(s => s === store);
        const todayHrs    = getTodayHours(store);
        const distText    = store._distKm < 10
          ? store._distKm.toFixed(1)
          : Math.round(store._distKm);
        const kmAway      = window.themeStrings?.kmAway           || 'km away';
        const openLabel   = window.themeStrings?.open              || 'Open';
        const closedLabel = window.themeStrings?.closed            || 'Closed';
        const todayLabel  = window.themeStrings?.today              || 'Today';

        const address       = `${store.address1}, ${store.city}, ${store.province} ${store.zip}`;
        const directionsUrl = buildDirectionsUrl(address, this._locale);
        const distKm    = store._distKm != null ? (Math.round(store._distKm * 100) / 100) : null;
        // Validate store_page_name before interpolating into href — prevents open
        // redirects if malformed data arrives from the store JSON endpoint.
        const safeName  = store.store_page_name && /^[\w-]+$/.test(store.store_page_name)
          ? store.store_page_name
          : null;
        const detailUrl = safeName
          ? routePath(`pages/cannabis-store-locator/${safeName}?store_index=${rank + 1}${distKm != null ? '&distance_km=' + distKm : ''}`)
          : '#';

        const li = cloneTemplate('store-locator-tile-template');
        li.tabIndex         = 0;
        li.dataset.storeIdx = globalIdx;

        li.querySelector('.store-location-tile__rank').textContent = String(rank + 1);
        li.querySelector('[data-sl="distance"]').textContent = `${distText} ${kmAway}`;
        li.querySelector('[data-sl="title"]').textContent = store.title;
        li.querySelector('[data-sl="address"]').textContent =
          `${store.address1}${store.address2 ? ', ' + store.address2 : ''}, ${store.city}, ${store.province} ${store.zip}`;

        const statusEl = li.querySelector('[data-sl="status"]');
        const hoursRangeEl = li.querySelector('[data-sl="hours-range"]');
        if (todayHrs) {
          statusEl.textContent = todayHrs.isOpen ? openLabel : closedLabel;
          statusEl.classList.add(todayHrs.isOpen ? 'store-location-tile__status--is-open' : 'store-location-tile__status--is-closed');
          hoursRangeEl.textContent = `${fmt12(todayHrs.open)} – ${fmt12(todayHrs.close)} (${todayLabel})`;
        } else {
          statusEl.textContent = closedLabel;
          statusEl.classList.add('store-location-tile__status--is-closed');
          hoursRangeEl.textContent = `(${todayLabel})`;
        }

        li.querySelector('[data-sl="directions"]').href = directionsUrl;

        // The template marks this target="_blank". When store_page_name fails
        // the safeName check above there is no page to open and detailUrl is
        // '#', which in a new tab would mean a blank one — so drop the target
        // and leave the existing in-place '#' behaviour alone. The info-window
        // template removes its equivalent link entirely in the same situation.
        const detailLinkEl = li.querySelector('[data-sl="detail"]');
        detailLinkEl.href = detailUrl;
        if (!safeName) {
          detailLinkEl.removeAttribute('target');
          detailLinkEl.removeAttribute('rel');
          // Drop the "opens in a new window" announcement with it, so screen
          // readers are not told about behaviour this link no longer has.
          detailLinkEl.querySelector('.visually-hidden')?.remove();
        }

        const phoneEl = li.querySelector('[data-sl="phone"]');
        if (store.formatted_phone) {
          phoneEl.href = `tel:${store.phone}`;
          phoneEl.textContent = store.formatted_phone;
        } else {
          phoneEl.remove();
        }

        li.addEventListener('click', (e) => {
          if (e.target.closest('a')) return;
          const marker = this._markers[globalIdx];
          if (marker) {
            this._suppressMapEvents();
            this._map.panTo(marker.getPosition());
            this._openInfoWindow(store, marker);
          }
          this._highlightCard(globalIdx);
        });

        li.addEventListener('keydown', (e) => {
          if (this._handleResultsTab(e, e.target)) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            li.click();
          }
        });

        li.addEventListener('mouseenter', () => {
          const marker = this._markers[globalIdx];
          if (marker?.getMap()) marker.setIcon(this._createPinIcon(marker._rank || 1, true));
        });

        li.addEventListener('mouseleave', () => {
          const marker = this._markers[globalIdx];
          if (marker?.getMap() && !li.classList.contains('is-active')) {
            marker.setIcon(marker._numberedIcon || this._iconRegular());
            marker.set('label', null);
          }
        });

        this._resultsList.appendChild(li);
      };

      displayList.forEach((store, rank) => {
        renderTile(store, rank);
      });
    }

    _observeMapKeyboardShortcutControls() {
      this._mapControlObserver?.disconnect();
      this._mapControlObserver = new MutationObserver(() => {
        this._dedupeMapKeyboardShortcutControls();
      });
      this._mapControlObserver.observe(this._mapEl, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['aria-label', 'tabindex'],
      });
      this._dedupeMapKeyboardShortcutControls();
    }

    _dedupeMapKeyboardShortcutControls() {
      const buttons = Array.from(this._mapEl.querySelectorAll('button[aria-label]'));
      const labelsWithVisibleControls = new Set(
        buttons
          .filter(button => button.textContent.trim())
          .map(button => button.getAttribute('aria-label'))
          .filter(Boolean)
      );

      // Google injects an empty keyboard-accessibility entry button before the
      // map and a visible Keyboard shortcuts button after the zoom controls.
      // They share one localized aria-label. Keep the visible help control and
      // remove only its empty duplicate from the page's Tab sequence.
      buttons.forEach(button => {
        const label = button.getAttribute('aria-label');
        if (!button.textContent.trim() && label && labelsWithVisibleControls.has(label)) {
          if (button.tabIndex !== -1) button.tabIndex = -1;
          if (!button.disabled) button.disabled = true;
          if (button.getAttribute('aria-hidden') !== 'true') {
            button.setAttribute('aria-hidden', 'true');
          }
        }
      });
    }

    _handleMapMarkerTab(event) {
      if (event.key !== 'Tab') return;

      const visibleTitles = new Set(
        this._markers
          .filter(marker => marker.getMap())
          .map(marker => marker._storeTitle)
          .filter(Boolean)
      );
      const markerElements = Array.from(this._mapEl.querySelectorAll('[title]'))
        .filter(element => visibleTitles.has(element.title));
      const currentIndex = markerElements.indexOf(document.activeElement);
      if (currentIndex < 0) return;

      const nextIndex = currentIndex + (event.shiftKey ? -1 : 1);
      if (nextIndex < 0 || nextIndex >= markerElements.length) return;

      event.preventDefault();
      event.stopPropagation();
      markerElements.forEach((element, index) => {
        element.tabIndex = index === nextIndex ? 0 : -1;
      });
      markerElements[nextIndex].focus();
    }

    // ── Highlight active card ────────────────────────────────────────────────
    _highlightCard(storeIdx) {
      // Update card highlight classes
      this._resultsList.querySelectorAll('.store-location-tile').forEach(el => {
        el.classList.remove('is-active');
        if (parseInt(el.dataset.storeIdx, 10) === storeIdx) {
          el.classList.add('is-active');

          // Highlight only on mobile/tablet. In the stacked layout the map sits
          // above the list, so scrolling the results panel moves content that is
          // largely off-screen — the user taps a pin, looks at the info window,
          // and the list has silently repositioned underneath. On desktop the
          // list is beside the map and in view, so scrolling the selected card
          // under the sticky header is the point.
          if (!isDesktopLayout()) return;

          const target = el;
          // Defer so measurements are correct after any list re-render (e.g. "See all" expand).
          requestAnimationFrame(() => {
            const panel = target.closest('.store-locator__results');
            // Scroll the actual results scroller and place the selected card just
            // below the sticky heading. The outer results-panel uses overflow:hidden.
            if (panel && panel.scrollHeight > panel.clientHeight + 1) {
              const panelRect = panel.getBoundingClientRect();
              const header = panel.querySelector('.store-locator__results-header');
              const headerRect = header && !header.hidden ? header.getBoundingClientRect() : null;
              const pin = target.querySelector('.store-location-tile__rank');
              const pinRect = pin ? pin.getBoundingClientRect() : target.getBoundingClientRect();
              // The tile pin is pulled above the card with negative margin, so
              // align the pin itself — not the card box — below the sticky header.
              const stickyBottom = headerRect ? Math.max(0, headerRect.bottom - panelRect.top) : 0;
              const desiredPinTop = stickyBottom + 8;
              const pinTop = panel.scrollTop + (pinRect.top - panelRect.top);
              const nextTop = Math.max(0, pinTop - desiredPinTop);
              panel.scrollTo({ top: nextTop, behavior: 'smooth' });
            }
          });
        }
      });

      // Swap icon only — labels were already set in _refreshResults.
      // Guard with getMap(): markers not currently on the map have no icon to swap.
      if (this._lastInRange) {
        this._lastInRange.forEach((store) => {
          const idx = store._originalIdx ?? this._stores.findIndex(s => s === store);
          if (idx === -1) return;
          if (!this._markers[idx]?.getMap()) return; // detached — nothing to update
          const isActive = idx === storeIdx;
          // Restore the canvas-composited numbered icon when deselecting — not the plain
          // PNG — so rank numbers are preserved on all non-active pins after a click.
          // _numberedIcon is stamped in _refreshResults; fall back to plain only if unset.
          // Active pin: composite the rank number over pin-selected.png so it stays
          // visible when the pin is in the clicked/highlighted state.
          // Inactive pins: restore their canvas-composited numbered icon, not the plain PNG.
          this._markers[idx].setIcon(
            isActive
              ? this._createPinIcon(this._markers[idx]._rank || 1, true)
              : (this._markers[idx]._numberedIcon || this._iconRegular())
          );
          // Clear any residual Maps label — rank numbers are now composited onto
          // the icon canvas in _refreshResults, not set via the label property.
          this._markers[idx].set('label', null);
        });
      }
    }

    // ── Open info window ─────────────────────────────────────────────────────
    _openInfoWindow(store, marker) {
      const todayHrs    = getTodayHours(store);
      const openLabel   = window.themeStrings?.open              || 'Open';
      const closedLabel = window.themeStrings?.closed            || 'Closed';
      const todayLabel  = window.themeStrings?.today              || 'Today';

      const hoursText = todayHrs
        ? `${todayHrs.isOpen ? openLabel : closedLabel} · ${fmt12(todayHrs.open)} – ${fmt12(todayHrs.close)} (${todayLabel})`
        : `${closedLabel} (${todayLabel})`;

      const address       = `${store.address1}, ${store.city}, ${store.province} ${store.zip}`;
      const directionsUrl = buildDirectionsUrl(address, this._locale);
      const rank     = this._lastInRange
        ? (store._originalIdx != null
            ? this._lastInRange.findIndex(s => s._originalIdx === store._originalIdx)
            : this._lastInRange.findIndex(s => s === store))
        : -1;
      const storeIdx = rank >= 0 ? rank + 1 : null;
      const distKm    = store._distKm != null ? (Math.round(store._distKm * 100) / 100) : null;
      // Validate store_page_name before interpolating into href — same guard as
      // _renderResultsList to prevent open redirects from malformed store data.
      const safeName  = store.store_page_name && /^[\w-]+$/.test(store.store_page_name)
        ? store.store_page_name
        : null;
      const detailUrl = safeName
        ? routePath(`pages/cannabis-store-locator/${safeName}${storeIdx != null ? '?store_index=' + storeIdx : ''}${distKm != null ? (storeIdx != null ? '&' : '?') + 'distance_km=' + distKm : ''}`)
        : null;

      const infoWindowEl = cloneTemplate('store-locator-infowindow-template');
      infoWindowEl.querySelector('[data-sl="title"]').textContent = store.title;
      infoWindowEl.querySelector('[data-sl="address"]').textContent =
        `${store.address1}${store.address2 ? ', ' + store.address2 : ''}, ${store.city}, ${store.province} ${store.zip}`;
      infoWindowEl.querySelector('[data-sl="hours"]').textContent = hoursText;
      infoWindowEl.querySelector('[data-sl="directions"]').href = directionsUrl;

      const detailEl = infoWindowEl.querySelector('[data-sl="detail"]');
      if (detailUrl) {
        detailEl.href = detailUrl;
      } else {
        detailEl.remove();
      }

      const phoneEl = infoWindowEl.querySelector('[data-sl="phone"]');
      if (store.formatted_phone) {
        phoneEl.href = `tel:${store.phone}`;
        phoneEl.textContent = store.formatted_phone;
      } else {
        phoneEl.remove();
      }

      // Size the bubble to the map before opening it. Recomputed per open rather
      // than once at construction so it follows orientation changes and the
      // mobile map toggle, both of which resize the map without re-creating it.
      // The CSS still caps the content at 260px above 1023px, so this only ever
      // adds room where the map is narrow.
      const mapWidth = this._mapEl?.clientWidth || 0;
      const gutter   = window.innerWidth < INFO_NARROW_VIEWPORT
        ? INFO_NARROW_GUTTER
        : INFO_MAP_GUTTER;
      // No lower bound: the result must never exceed the map, and a floor would
      // do exactly that on the narrowest screens — the ones that need the width
      // most. Desktop does not need one, since the CSS caps the content anyway.
      if (mapWidth > gutter) {
        this._infoWindow.setOptions({ maxWidth: Math.min(INFO_MAX_WIDTH, mapWidth - gutter) });
      }

      this._infoWindow.setContent(infoWindowEl);
      this._infoWindow.open(this._map, marker);
    }
  }

  customElements.define('store-locator-map', StoreLocatorMap);
}());
